// A palette in the roles of Weblium's palette settings, in the order the chat shows and sends them.
export const PALETTE_ROLES = ['lightBase', 'lightAccent', 'brand', 'darkAccent', 'darkBase'] as const;
export type PaletteRoleType = (typeof PALETTE_ROLES)[number];

export const DEFAULT_PALETTE: string[] = ['#FFFFFF', '#F1F5F9', '#2563EB', '#1E3A8A', '#0F172A'];

// Text on a surface is readable from about 4.5:1; below that between the two bases the site's text would struggle.
export const MIN_BASE_CONTRAST = 4.5;

/** "abc", "#abc", "aabbcc" and "#AABBCC" all become "#AABBCC"; anything else is not a colour. */
export const normalizeHex = (raw: string): string | null => {
  const value = raw.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(value)) {
    return `#${value
      .split('')
      .map((digit) => digit + digit)
      .join('')
      .toUpperCase()}`;
  }
  return /^[0-9a-f]{6}$/i.test(value) ? `#${value.toUpperCase()}` : null;
};

const channel = (value: number): number => {
  const unit = value / 255;
  return unit <= 0.03928 ? unit / 12.92 : ((unit + 0.055) / 1.055) ** 2.4;
};

const luminance = (hex: string): number => {
  const value = Number.parseInt(hex.slice(1), 16);
  return 0.2126 * channel((value >> 16) & 255) + 0.7152 * channel((value >> 8) & 255) + 0.0722 * channel(value & 255);
};

/** WCAG contrast ratio of two #RRGGBB colours, from 1 to 21. */
export const contrastRatio = (first: string, second: string): number => {
  const [lighter, darker] = [luminance(first), luminance(second)].sort((left, right) => right - left);
  return (lighter + 0.05) / (darker + 0.05);
};

/** A card option that carries a full five-colour scheme, or the neutral default when it does not. */
export const paletteFromSwatches = (swatches: string[] | undefined): string[] =>
  swatches && swatches.length === PALETTE_ROLES.length && swatches.every((color) => normalizeHex(color))
    ? swatches.map((color) => normalizeHex(color) as string)
    : [...DEFAULT_PALETTE];
